import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { X } from 'lucide-react';
import { useState } from 'react';
import { Input } from '@/shared/components/ui/input';
import { Checkbox } from '@/shared/components/ui/checkbox';
import type { ProviderEditorFormData } from '../schemas';
import { usePayerOptions } from '../../../hooks/useReferenceData';

type PaymentOptionsSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
};

/**
 * ArrayFieldManager - Reusable component for managing array fields
 */
function ArrayFieldManager({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string[];
  onChange: (newValue: string[]) => void;
  placeholder: string;
  hint?: string;
}) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>

      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
          Add
        </Button>
      </div>

      {hint && <p className="text-xs text-gray-500">{hint}</p>}

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((item, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1 text-sm text-blue-700"
            >
              <span>{item}</span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="text-blue-500 hover:text-blue-700"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PaymentOptionsSection - Payment methods and payer sources
 * 
 * Contains:
 * - Payer Options (many-to-many): Structured list from reference data (Medicare, Medicaid, etc.)
 * - Payer Sources (array field): Free-form list for additional payment methods not in reference data
 */
export function PaymentOptionsSection({
  control,
  errors,
}: PaymentOptionsSectionProps) {
  // Fetch payer options from reference data
  const { data: payerOptions, isLoading: isLoadingPayerOptions } = usePayerOptions();

  // Suppress unused errors for now
  void errors;

  return (
    <div className="space-y-6">
      {/* Payer Options (structured multi-select from reference data) */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700">
          Accepted Payment Methods
          <span className="ml-1 text-xs font-normal text-gray-500">
            (Select from standard options)
          </span>
        </Label>

        {isLoadingPayerOptions ? (
          <div className="text-sm text-gray-500">Loading payment options...</div>
        ) : (
          <Controller
            name="payerOptionIds"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {payerOptions?.map((option) => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`payer-option-${option.id}`}
                      checked={field.value?.includes(option.id)}
                      onCheckedChange={(checked) => {
                        const currentValue = field.value || [];
                        if (checked) {
                          field.onChange([...currentValue, option.id]);
                        } else {
                          field.onChange(
                            currentValue.filter((id) => id !== option.id)
                          );
                        }
                      }}
                    />
                    <label
                      htmlFor={`payer-option-${option.id}`}
                      className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {option.name}
                    </label>
                  </div>
                ))}
              </div>
            )}
          />
        )}

        <p className="text-xs text-gray-500">
          Select all payment methods and payer sources that this provider accepts
          (e.g., Medicare, Medicaid, Private Pay, Veterans Benefits, etc.)
        </p>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Payer Sources (free-form array for additional payment methods) */}
      <Controller
        name="provider.payerSources"
        control={control}
        render={({ field }) => (
          <ArrayFieldManager
            label="Additional Payment Sources"
            value={field.value || []}
            onChange={field.onChange}
            placeholder="Enter custom payment method"
            hint="Add any additional payment methods not listed above (optional)"
          />
        )}
      />
    </div>
  );
}

